import React, { useMemo } from 'react';
import { 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  GitBranch, 
  QrCode, 
  Printer, 
  Menu as MenuIcon,
  RefreshCw,
} from 'lucide-react';
import { Restaurant, RestaurantPagination, RestaurantLogo } from '../../../services/api';
import { API_BASE_URL } from '../../../config/api';
import { PlanBadge } from '../PlanBadge';

interface RestaurantsViewProps {
  restaurants: Restaurant[];
  pagination: RestaurantPagination | null;
  restaurantPage: number;
  restaurantFilterPlan: string;
  restaurantFilterActive: boolean | undefined;
  onPageChange: (page: number) => void;
  onFilterPlanChange: (plan: string) => void;
  onFilterActiveChange: (active: boolean | undefined) => void;
  onCreateNew: () => void;
  onEdit: (restaurant: Restaurant) => void;
  onViewUsers: (restaurant: Restaurant) => void;
  onViewBranches: (restaurant: Restaurant) => void;
  onViewQRCodes: (restaurant: Restaurant) => void;
  onTestPrint: (restaurant: Restaurant) => void;
  onViewMenu: (restaurant: Restaurant) => void;
  onClearData: (restaurant: Restaurant) => void;
  onDelete: (restaurant: Restaurant) => void;
}

const getLogoUrl = (restaurant: Restaurant): string | null => {
  if (!restaurant) return null;
  
  // Check logos array first (multi-logo support)
  if (restaurant.logos && Array.isArray(restaurant.logos) && restaurant.logos.length > 0) {
    // Use the first regular logo, or first logo if no regular logo exists
    const regularLogo = restaurant.logos.find((logo: RestaurantLogo) => logo?.type === 'regular' || !logo?.type);
    return regularLogo?.url || restaurant.logos[0]?.url || null;
  }
  
  // Fallback to logoUrl if logos array is empty or invalid
  if (restaurant.logoUrl && typeof restaurant.logoUrl === 'string' && restaurant.logoUrl.trim() !== '') {
    const logo = restaurant.logoUrl.trim();
    if (logo.startsWith('http://') || logo.startsWith('https://')) {
      return logo;
    } else if (logo.startsWith('/')) {
      return `${API_BASE_URL}${logo}`;
    } else {
      return `${API_BASE_URL}/uploads/${logo}`;
    }
  }
  
  return null;
};

export const RestaurantsView = React.memo(function RestaurantsView({
  restaurants,
  pagination,
  restaurantPage,
  restaurantFilterPlan,
  restaurantFilterActive,
  onPageChange,
  onFilterPlanChange,
  onFilterActiveChange,
  onCreateNew,
  onEdit,
  onViewUsers,
  onViewBranches,
  onViewQRCodes,
  onTestPrint,
  onViewMenu,
  onClearData,
  onDelete,
}: RestaurantsViewProps): React.JSX.Element {
  const hasFilters = useMemo(() => 
    Boolean(restaurantFilterPlan || restaurantFilterActive !== undefined),
    [restaurantFilterPlan, restaurantFilterActive]
  );

  const handleFilterPlanChange = useMemo(() => (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFilterPlanChange(e.target.value);
    onPageChange(1);
  }, [onFilterPlanChange, onPageChange]);

  const handleFilterActiveChange = useMemo(() => (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onFilterActiveChange(value === '' ? undefined : value === 'true');
    onPageChange(1);
  }, [onFilterActiveChange, onPageChange]);

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">Restaurants</h2>
        <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600">Manage all restaurants on the platform</p>
      </div>
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <button
          onClick={onCreateNew}
          className="flex items-center justify-center gap-2 bg-gradient-to-r from-amber-600 to-amber-500 text-white px-4 py-2.5 rounded-lg hover:from-amber-500 hover:to-amber-600 transition-all font-medium text-sm sm:text-base"
        >
          <Plus className="w-5 h-5" />
          <span>New Restaurant</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-4 mb-6 shadow-xl">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
          <select
            value={restaurantFilterPlan}
            onChange={handleFilterPlanChange}
            className="flex-1 sm:flex-none px-4 py-2.5 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm sm:text-base"
          >
            <option value="">All Plans</option>
            <option value="SILVER">SILVER</option>
            <option value="GOLD">GOLD</option>
            <option value="PLATINUM">PLATINUM</option>
          </select>
          <select
            value={restaurantFilterActive === undefined ? '' : restaurantFilterActive.toString()}
            onChange={handleFilterActiveChange}
            className="flex-1 sm:flex-none px-4 py-2.5 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm sm:text-base"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      {restaurants.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-24 h-24 bg-gradient-to-br from-amber-600/20 to-amber-500/20 dark:from-amber-600/20 dark:to-amber-500/20 from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center mb-6">
            <Building2 className="w-12 h-12 text-amber-500" />
          </div>
          <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">No restaurants found</h3>
          <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm mb-6 text-center max-w-md">
            {hasFilters
              ? 'Try adjusting your filters to see more results.' 
              : 'Get started by creating your first restaurant.'}
          </p>
          {!hasFilters && (
            <button
              onClick={onCreateNew}
              className="flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white px-6 py-3 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl font-medium"
            >
              <Plus className="w-5 h-5" />
              Create Restaurant
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4 sm:gap-6">
          {restaurants.map((restaurant) => {
            if (!restaurant?._id) return null;
            const logoUrl = getLogoUrl(restaurant);
            
            return (
              <div
                key={restaurant._id}
                className="group bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-5 sm:p-7 shadow-xl hover:shadow-2xl hover:shadow-amber-600/10 dark:hover:shadow-amber-600/10 transition-all duration-300 hover:border-amber-600/40 dark:hover:border-amber-600/40 hover:border-amber-500/40"
              >
                {/* Restaurant Logo */}
                <div className="flex items-center justify-center mb-5">
                  {logoUrl ? (
                    <div className="relative w-24 h-24 rounded-xl overflow-hidden">
                      <img 
                        src={logoUrl || ''} 
                        alt={restaurant?.name || 'Restaurant logo'}
                        className="w-full h-full object-contain"
                        loading="lazy"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            const fallback = parent.querySelector('.logo-fallback') as HTMLElement;
                            if (fallback) {
                              fallback.classList.remove('hidden');
                              fallback.classList.add('flex');
                            }
                          }
                        }}
                      />
                      <div className="absolute inset-0 w-full h-full hidden logo-fallback items-center justify-center bg-gradient-to-br from-amber-600 to-amber-500">
                        <Building2 className="w-12 h-12 text-white" />
                      </div>
                    </div>
                  ) : (
                    <div className="w-24 h-24 bg-gradient-to-br from-amber-600 to-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-600/30">
                      <Building2 className="w-12 h-12 text-white" />
                    </div>
                  )}
                </div>
                
                <div className="flex items-start justify-between mb-5">
                  <div className="flex-1 w-full">
                    <h3 className="text-xl sm:text-2xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-3 text-center">{restaurant.name}</h3>
                    <div className="flex items-center gap-2 flex-wrap justify-center">
                      <PlanBadge plan={restaurant.plan || 'SILVER'} size="sm" />
                      <span
                        className={`px-2 py-1 rounded-lg text-xs font-medium ${
                          restaurant.isActive
                            ? 'bg-green-500/20 dark:bg-green-500/20 bg-green-100 text-green-400 dark:text-green-400 text-green-700'
                            : 'bg-red-500/20 dark:bg-red-500/20 bg-red-100 text-red-400 dark:text-red-400 text-red-700'
                        }`}
                      >
                        {restaurant.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {restaurant.packageEndDate && (
                        <span className="px-2 py-1 bg-blue-500/20 dark:bg-blue-500/20 bg-blue-100 text-blue-400 dark:text-blue-400 text-blue-700 rounded-lg text-xs font-medium">
                          Expires: {new Date(restaurant.packageEndDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mt-5">
                  <button
                    onClick={() => onEdit(restaurant)}
                    className="flex items-center justify-center gap-2 bg-blue-600/20 dark:bg-blue-600/20 bg-blue-100 hover:bg-blue-600/30 dark:hover:bg-blue-600/30 hover:bg-blue-200 text-blue-400 dark:text-blue-400 text-blue-700 px-3 py-2.5 rounded-xl transition-all text-sm font-medium border border-blue-600/20 dark:border-blue-600/20 border-blue-300/20 hover:border-blue-600/40 dark:hover:border-blue-600/40 hover:border-blue-500/40"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => onViewUsers(restaurant)}
                    className="flex items-center justify-center gap-2 bg-purple-600/20 dark:bg-purple-600/20 bg-purple-100 hover:bg-purple-600/30 dark:hover:bg-purple-600/30 hover:bg-purple-200 text-purple-400 dark:text-purple-400 text-purple-700 px-3 py-2.5 rounded-xl transition-all text-sm font-medium border border-purple-600/20 dark:border-purple-600/20 border-purple-300/20 hover:border-purple-600/40 dark:hover:border-purple-600/40 hover:border-purple-500/40"
                  >
                    <Users className="w-4 h-4" />
                    <span>Users</span>
                  </button>
                  <button
                    onClick={() => onViewBranches(restaurant)}
                    className="flex items-center justify-center gap-2 bg-green-600/20 dark:bg-green-600/20 bg-green-100 hover:bg-green-600/30 dark:hover:bg-green-600/30 hover:bg-green-200 text-green-400 dark:text-green-400 text-green-700 px-3 py-2.5 rounded-xl transition-all text-sm font-medium border border-green-600/20 dark:border-green-600/20 border-green-300/20 hover:border-green-600/40 dark:hover:border-green-600/40 hover:border-green-500/40"
                  >
                    <GitBranch className="w-4 h-4" />
                    <span>Branches</span>
                  </button>
                  <button
                    onClick={() => onViewQRCodes(restaurant)}
                    className="flex items-center justify-center gap-2 bg-amber-600/20 dark:bg-amber-600/20 bg-amber-100 hover:bg-amber-600/30 dark:hover:bg-amber-600/30 hover:bg-amber-200 text-amber-400 dark:text-amber-400 text-amber-700 px-3 py-2.5 rounded-xl transition-all text-sm font-medium border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 hover:border-amber-600/40 dark:hover:border-amber-600/40 hover:border-amber-500/40"
                  >
                    <QrCode className="w-4 h-4" />
                    <span>QR Codes</span>
                  </button>
                  <button
                    onClick={() => onTestPrint(restaurant)}
                    className="flex items-center justify-center gap-2 bg-indigo-600/20 dark:bg-indigo-600/20 bg-indigo-100 hover:bg-indigo-600/30 dark:hover:bg-indigo-600/30 hover:bg-indigo-200 text-indigo-400 dark:text-indigo-400 text-indigo-700 px-3 py-2.5 rounded-xl transition-all text-sm font-medium border border-indigo-600/20 dark:border-indigo-600/20 border-indigo-300/20 hover:border-indigo-600/40 dark:hover:border-indigo-600/40 hover:border-indigo-500/40"
                  >
                    <Printer className="w-4 h-4" />
                    <span>Test Print</span>
                  </button>
                  <button
                    onClick={() => onViewMenu(restaurant)}
                    className="flex items-center justify-center gap-2 bg-green-600/20 dark:bg-green-600/20 bg-green-100 hover:bg-green-600/30 dark:hover:bg-green-600/30 hover:bg-green-200 text-green-400 dark:text-green-400 text-green-700 px-3 py-2.5 rounded-xl transition-all text-sm font-medium border border-green-600/20 dark:border-green-600/20 border-green-300/20 hover:border-green-600/40 dark:hover:border-green-600/40 hover:border-green-500/40"
                  >
                    <MenuIcon className="w-4 h-4" />
                    <span>Menu Management</span>
                  </button>
                  <button
                    onClick={() => onClearData(restaurant)}
                    className="flex items-center justify-center gap-2 bg-orange-600/20 dark:bg-orange-600/20 bg-orange-100 hover:bg-orange-600/30 dark:hover:bg-orange-600/30 hover:bg-orange-200 text-orange-400 dark:text-orange-400 text-orange-700 px-3 py-2.5 rounded-xl transition-all text-sm font-medium border border-orange-600/20 dark:border-orange-600/20 border-orange-300/20 hover:border-orange-600/40 dark:hover:border-orange-600/40 hover:border-orange-500/40 col-span-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Delete Data Only</span>
                  </button>
                  <button
                    onClick={() => onDelete(restaurant)}
                    className="flex items-center justify-center gap-2 bg-red-600/20 dark:bg-red-600/20 bg-red-100 hover:bg-red-600/30 dark:hover:bg-red-600/30 hover:bg-red-200 text-red-400 dark:text-red-400 text-red-700 px-3 py-2.5 rounded-xl transition-all text-sm font-medium border border-red-600/20 dark:border-red-600/20 border-red-300/20 hover:border-red-600/40 dark:hover:border-red-600/40 hover:border-red-500/40 col-span-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete Restaurant</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => onPageChange(Math.max(1, restaurantPage - 1))}
            disabled={restaurantPage <= 1}
            aria-label="Previous page"
            className="px-4 py-2 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
          >
            Previous
          </button>
          <span className="text-zinc-300 dark:text-zinc-300 text-zinc-700 font-medium px-4">
            Page {pagination.page || 1} of {pagination.pages || 1}
          </span>
          <button
            onClick={() => onPageChange(Math.min(pagination.pages || 1, restaurantPage + 1))}
            disabled={restaurantPage >= (pagination.pages || 1)}
            aria-label="Next page"
            className="px-4 py-2 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
});

